import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAlertService } from '@/shared/lib/useAlertService'
import { useAuth } from '@/shared/context/AuthContext'
import { ChildrenService } from '@/shared/api/rest/services/children.service'
import { resizeAndUploadImage } from '@/shared/utils/imageUploadUtility'
import {
    getFromLocalStorage,
    deleteFromLocalStorage
} from '@/shared/utils/localStorageHelper'
import createChildrenSchema from '../register-child-page/form-schema'

export default function useRegisterChild(onSuccess: () => void) {
    const alertService = useAlertService()
    const { user } = useAuth()
    const queryClient = useQueryClient()

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: yupResolver(createChildrenSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            dateOfBirth: new Date(),
            picture: '',
            allergies: '',
            gender: 'male',
            bloodGroup: undefined
        }
    })

    const { mutateAsync: createChildren } = useMutation({
        mutationFn: ChildrenService.create,
        onSuccess: () => {
            alertService.success('Child registered successfully')
            reset()
            queryClient.invalidateQueries({ queryKey: ['my-children'] })
            onSuccess()
        },
        onError: (error: Error) => {
            alertService.error(error.message, error.name)
        }
    })

    const submitHandler = async (formValue: any) => {
        const imageUrl = getFromLocalStorage('image-url')
        const picture = await resizeAndUploadImage(formValue, imageUrl)

        const allergies =
            typeof formValue.allergies === 'string'
                ? formValue.allergies
                      .split(',')
                      .map((a: string) => a.trim())
                      .filter(Boolean)
                : formValue.allergies

        const dob =
            formValue.dateOfBirth instanceof Date
                ? formValue.dateOfBirth.toISOString()
                : formValue.dateOfBirth

        try {
            await createChildren({
                firstName: formValue.firstName,
                lastName: formValue.lastName,
                middleName: formValue.middleName,
                dateOfBirth: dob,
                gender: formValue.gender,
                bloodGroup: formValue.bloodGroup,
                allergies,
                picture: picture || '',
                parents: user?.parent?._id ? [user.parent._id] : undefined
            })
            deleteFromLocalStorage('image-url')
        } catch (error) {
            if (error instanceof Error) {
                alertService.error(error.message, error.name)
            }
        }
    }

    return {
        isSubmitting,
        errors,
        register,
        handleSubmit: handleSubmit(submitHandler)
    }
}
